import { useEffect, useCallback, useRef } from 'react';

import { useLazyGetClientServicesByEmployeeIdQuery } from '@app/api/ClientServices';
import {
  useLazyGetUserByIdQuery,
  useLazyGetUserSettingsQuery,
} from '@app/api/User/api';
import {
  WARNING,
  INFO_DATA,
  ADDITIONAL_INFO_DATA,
  SETTINGS_DATA,
  SERVICE_DATA,
  ACCESS_RIGHTS_DATA,
} from '@app/constants';
import { useMemoizeObject } from '@app/hooks';
import type { NTabsNotificationProvider } from '@app/providers/TabsNotificationProvider/TabsNotificationProvider.types';
import { handleCatchError } from '@app/services';
import type { NUsers } from '@app/types';
import { keysFirstLetterToLower } from '@app/utils';

import type { NEditEmployee } from '@pages/Employees/EditEmployee/EditEmployee.types';

export const useEditEmployeeEffects = ({
  userId,
  database,
  activeBranchId,
  setActiveKey,
  initialTab,
  editableEmployeeId,
  copyingServicesFromEmployeeId,
  employee,
  allServices,
  activeKey,
  accessRights,
  memoizedAllSettings,
  memoizedInitialAccessRights,
  setEmployee,
  setTitleData,
  setAllSettings,
  setAllServices,
  setServicesLoadState,
  showNotification,
  setGlobalLoader,
  setInfoData,
  setOriginalData,
  setNewData,
  setTabInfo,
  getUserAccessRightsHandler,
}: NEditEmployee.UseEditEmployeeEffects) => {
  const memoizedEmployee = useMemoizeObject(employee);
  const memoizedAllServices = useMemoizeObject(allServices);
  const memoizedAccessRights = useMemoizeObject(accessRights);
  const [getUserById] = useLazyGetUserByIdQuery();
  const [getUserSettings] = useLazyGetUserSettingsQuery();
  const [getClientServicesByEmployeeId] =
    useLazyGetClientServicesByEmployeeIdQuery();

  useEffect(() => {
    setActiveKey(String(initialTab));
  }, [initialTab, setActiveKey]);

  useEffect(() => {
    getUserAccessRightsHandler();
  }, [getUserAccessRightsHandler]);

  const getDataRequestIdRef = useRef(0);

  const getData = useCallback(async () => {
    if (userId && database && activeBranchId) {
      // Ответ устаревшего запроса (например, после ухода с вкладки во время
      // загрузки копии) не должен перезаписывать данные актуального
      const requestId = ++getDataRequestIdRef.current;
      const isActualRequest = () => requestId === getDataRequestIdRef.current;

      // При копировании услуг показываем услуги исполнителя-источника —
      // они заменят услуги текущего исполнителя после сохранения
      const isCopyingServices =
        Boolean(copyingServicesFromEmployeeId) &&
        copyingServicesFromEmployeeId !== editableEmployeeId;

      // Пока услуги не загружены, в allServices лежит прежний список (например,
      // отменённая копия), поэтому сохранение блокируется до успешной загрузки
      setServicesLoadState({ status: 'loading', isCopying: isCopyingServices });

      try {
        const fetchedUser = await getUserById({
          getUserId: editableEmployeeId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (!isActualRequest()) return;

        const adaptedUser = keysFirstLetterToLower<NUsers.User>(fetchedUser);

        setEmployee(adaptedUser);
        setTitleData(
          `${adaptedUser?.nameLast || ''} ${adaptedUser.nameFirst}`.trim()
        );

        const settings = await getUserSettings({
          forUserId: editableEmployeeId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (!isActualRequest()) return;

        setAllSettings(settings);

        const services = await getClientServicesByEmployeeId({
          ActiveBranchId: activeBranchId,
          employeeId: isCopyingServices
            ? copyingServicesFromEmployeeId
            : editableEmployeeId,
        }).unwrap();

        if (!isActualRequest()) return;

        setAllServices(
          services.map((service) => ({
            ...service,
            isAddingFromModal: isCopyingServices,
            isDeletedAtClientSide: false,
          }))
        );

        setServicesLoadState({
          status: 'success',
          isCopying: isCopyingServices,
        });
      } catch (error) {
        if (!isActualRequest()) return;

        setServicesLoadState({ status: 'error', isCopying: isCopyingServices });

        showNotification(handleCatchError(error), WARNING);
      } finally {
        if (isActualRequest()) {
          setGlobalLoader(false);
        }
      }
    }
  }, [
    activeBranchId,
    database,
    editableEmployeeId,
    copyingServicesFromEmployeeId,
    setAllServices,
    setServicesLoadState,
    setAllSettings,
    setEmployee,
    setGlobalLoader,
    setTitleData,
    showNotification,
    userId,
    getUserById,
    getUserSettings,
    getClientServicesByEmployeeId,
  ]);

  useEffect(() => {
    getData();

    return () => setTitleData('');
  }, [getData, setTitleData]);

  useEffect(() => {
    if (!memoizedAllServices || !memoizedAllSettings) {
      return;
    }

    setInfoData(memoizedEmployee);

    const data = {
      additionalInfoData: memoizedEmployee,
      infoData: memoizedEmployee,
      serviceData: memoizedAllServices,
      settingsData: memoizedAllSettings,
      accessRightsData: memoizedInitialAccessRights,
    };

    setOriginalData(data);
    setNewData(data);

    const tabInfoMapping: Record<string, string> = {
      '2': INFO_DATA,
      '3': ADDITIONAL_INFO_DATA,
      '4': SETTINGS_DATA,
      '5': SERVICE_DATA,
      '8': ACCESS_RIGHTS_DATA,
    };

    setTabInfo(tabInfoMapping[activeKey]);
  }, [
    activeKey,
    memoizedEmployee,
    memoizedAllServices,
    memoizedInitialAccessRights,
    memoizedAllSettings,
    setOriginalData,
    setNewData,
    setTabInfo,
    setInfoData,
  ]);

  useEffect(() => {
    setNewData((prev: NTabsNotificationProvider.Context['newData']) => {
      return {
        ...prev,
        accessRightsData: memoizedAccessRights,
      };
    });
  }, [memoizedAccessRights, setNewData]);

  return { getData };
};
